import { useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import {
  useCreateCategoryMutation,
  useGetAllCategories,
} from "../../../../../api/jeep-rally/categories/categories.hooks";
import { useGetAllTypes } from "../../../../../api/jeep-rally/types/types.hooks";
import { normalizeTypes } from "../../Types/utils/type.utils";
import CategoryForm from "../_components/CategoryForm";
import {
  buildCategoryFormData,
  getCategoryApiErrorMessage,
  initialCategoryForm,
  isJeepRallyAdmin,
  normalizeCategories,
  validateCategoryForm,
} from "../utils/category.utils";

const CategoryCreatePage = () => {
  const history = useHistory();
  const canManage = isJeepRallyAdmin();
  const [form, setForm] = useState(initialCategoryForm);
  const [validationErrors, setValidationErrors] = useState({});

  const { data: categoriesData } = useGetAllCategories();
  const categories = useMemo(
    () => normalizeCategories(categoriesData),
    [categoriesData]
  );

  const {
    data: typesData,
    isLoading: isTypesLoading,
    isError: isTypesError,
  } = useGetAllTypes();
  const types = useMemo(() => normalizeTypes(typesData), [typesData]);

  const {
    mutate: createCategory,
    isLoading,
    isPending,
    error,
    reset,
  } = useCreateCategoryMutation();

  const submitting = isLoading || isPending;

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
    setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canManage) return;
    reset();

    const nextErrors = validateCategoryForm(form, {
      existingCategories: categories,
    });
    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    createCategory(buildCategoryFormData(form, { isEdit: false }), {
      onSuccess: () => {
        history.push("/jeep-rally/categories");
      },
    });
  };

  const goBack = () => history.push("/jeep-rally/categories");

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={goBack}
              aria-label="Back to categories list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">Create Category</h4>
          </div>
        </div>

        <div className="card-body">
          <p className="text-muted mb-4">
            Create a category and optionally link it to a type. The unique key
            is generated by the backend from the title.
          </p>

          {!canManage && (
            <Alert variant="warning" className="mb-4">
              Admin access is required to create categories.
            </Alert>
          )}

          {error && (
            <Alert variant="danger" className="mb-4">
              {getCategoryApiErrorMessage(
                error,
                "Failed to create category. Please try again."
              )}
            </Alert>
          )}

          <CategoryForm
            form={form}
            validationErrors={validationErrors}
            submitting={submitting}
            submitLabel="Create Category"
            submittingLabel="Creating..."
            readOnly={!canManage}
            types={types}
            isTypesLoading={isTypesLoading}
            isTypesError={isTypesError}
            onSubmit={handleSubmit}
            onFieldChange={updateField}
            onBack={goBack}
          />
        </div>
      </div>
    </div>
  );
};

export default CategoryCreatePage;
